import React, { useEffect, useState } from 'react';
import { Flashbar, SpaceBetween, Spinner, Tabs } from '@amzn/awsui-components-react-v3';
import { WorkspaceResourceStatus } from 'src/components/workspaces/workspaceManagement/resourceStatus';
import { auditWorkspace, getWorkspace, syncWorkspace } from 'src/api/permissions';
import { ClusterManagement } from 'src/components/workspaces/workspaceManagement/clusterManagement';
import { ManageRoles } from 'src/components/groups/manageRoles';
import { PageHeader } from 'src/components/common/PageHeader';
import { Workspace } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { createUpdateWorkspaceLink } from 'src/routes';
import { TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { WorkspaceDetailContainer } from 'src/components/workspaces/common/WorkspaceDetailContainer';
import { CommonPageProps } from 'src/components/common/CommonPageProps';

export interface WorkspaceDetailProps extends CommonPageProps {
  tabId: string | undefined;
  setContentType: any;
  match: any;
  activeGroup: string;
  workspace: Workspace;
}

export const WorkspaceDetail = (props: WorkspaceDetailProps) => {
  const [activeTabId, setActiveTabId] = useState('detail');
  const [workspace, setWorkspace] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const [auditButtonLoading, setAuditButtonLoading] = useState(false);
  const [syncButtonLoading, setSyncButtonLoading] = useState(false);
  const [refreshButtonLoading, setRefreshButtonLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [disableButton, setDisableButton] = useState(false);
  useEffect(() => {
    handleRefresh();
  }, []);

  const handleRefresh = async () => {
    props.setContentType(TABLE_CONTENT_TYPE);
    if (!props.activeGroup && !props.match.params.workspaceId) return;
    setLoading(true);
    setRefreshButtonLoading(true);
    try {
      let getWorkspaceResponse = await getWorkspace({ workspaceId: props.match.params.workspaceId });
      setWorkspace(getWorkspaceResponse.workspace);
    } catch (err) {
      console.log('Exception when get workspace for group', err);
    } finally {
      setLoading(false);
      setRefreshButtonLoading(false);
      setDisableButton(false);
    }
  };

  const tabs = [
    {
      label: 'Detail',
      id: 'detail',
      content: workspace !== undefined && <WorkspaceDetailContainer workspace={workspace} />,
    },
    {
      label: 'Resource status',
      id: 'resourceStatus',
      content: (
        <SpaceBetween size={'s'} direction={'vertical'}>
          <WorkspaceResourceStatus
            {...props}
            service={workspace?.workspaceConfig?.serviceConfig['FoundryCatalogManager']}
            setActiveTabId={setActiveTabId}
            name={'FoundryCatalogManager'}
          />
          <WorkspaceResourceStatus
            {...props}
            service={workspace?.workspaceConfig?.serviceConfig['FoundryAccessLogging']}
            setActiveTabId={setActiveTabId}
            name={'FoundryAccessLogging'}
          />
          <WorkspaceResourceStatus
            {...props}
            service={workspace?.workspaceConfig?.serviceConfig['FoundryAnalyticsManager']}
            setActiveTabId={setActiveTabId}
            name={'FoundryAnalyticsManager'}
          />
        </SpaceBetween>
      ),
    },
    {
      label: 'Cluster management',
      id: 'clusterManagement',
      content: workspace !== undefined && (
        <ClusterManagement {...props} workspace={workspace} setActiveTabId={setActiveTabId} refresh={handleRefresh} />
      ),
    },
    {
      label: 'Roles',
      id: 'roles',
      content: workspace !== undefined && (
        <ManageRoles
          ownerId={props.match.params.workspaceId}
          roleMapping={workspace ? workspace.roleMapping : undefined}
        />
      ),
    },
  ];

  const performEditWorkspace = () => {
    props.setRedirect(createUpdateWorkspaceLink(props.match.params.workspaceId));
  };

  const performAuditWorkspace = () => {
    setAuditButtonLoading(true);
    setNotifications([
      {
        type: 'success',
        content: `Start audit workspace.`,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
    setDisableButton(true);
    try {
      auditWorkspace({ workspaceId: workspace?.workspaceId });
      setAuditButtonLoading(false);
    } catch (err) {
      console.log('Exception when audit workspace', err);
      setNotifications([
        {
          type: 'error',
          content: err.message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
      setAuditButtonLoading(false);
    }
  };

  const performSyncWorkspace = () => {
    setSyncButtonLoading(true);
    setNotifications([
      {
        type: 'success',
        content: `Start sync workspace.`,
        dismissible: true,
        onDismiss: () => setNotifications([]),
      },
    ]);
    setDisableButton(true);
    try {
      syncWorkspace({ workspaceId: workspace?.workspaceId });
      setSyncButtonLoading(false);
    } catch (err) {
      console.log('Exception when sync workspace', err);
      setNotifications([
        {
          type: 'error',
          content: err.message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
      setSyncButtonLoading(false);
    }
  };

  const handleChange = async (e) => {
    await setActiveTabId(e.detail.activeTabId);
  };

  if (loading || !workspace) {
    return <Spinner size='large' />;
  }

  return (
    <SpaceBetween size={'l'} direction={'vertical'}>
      {notifications?.length > 0 && <Flashbar items={notifications}></Flashbar>}
      <PageHeader
        buttons={[
          {
            text: '',
            icon: 'refresh',
            onItemClick: handleRefresh,
            loading: refreshButtonLoading,
          },
          {
            text: 'CloudFormation drift check',
            onItemClick: performAuditWorkspace,
            disabled:
              workspace?.status == 'Active_Syncing' ||
              workspace?.status == 'Active_Auditing' ||
              workspace?.status == 'Active_Running' ||
              disableButton,
            loading: auditButtonLoading,
          },
          {
            text: 'Sync workspace',
            onItemClick: performSyncWorkspace,
            disabled:
              workspace?.status == 'Active_Syncing' ||
              workspace?.status == 'Active_Auditing' ||
              workspace?.status == 'Active_Running' ||
              disableButton,
            loading: syncButtonLoading,
          },
          {
            text: 'Edit',
            onItemClick: performEditWorkspace,
          },
        ]}
        header={workspace?.workspaceName}
        description={`AWS account ID: ${workspace?.accountId}`}
        variant={'h1'}
      />
      <Tabs tabs={tabs} variant='default' onChange={handleChange} activeTabId={activeTabId} />
    </SpaceBetween>
  );
};
